<template>
  <component
    :is="tag"
    :type="type"
    :size="size"
    :href="href"
    :to="to"
    :target="target"
    :plain="plain"
    :class="className"
    :disabled="disabled"
  >
    <slot />
  </component>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { ElButton } from 'element-plus';
import { useCssModule } from 'vue';
const style = useCssModule();
const props = defineProps({
  type: {
    validator(value: string) {
      return ['blue', 'black', 'gray', 'red', 'green'].indexOf(value) > -1;
    },
    type: String,
    default: 'black'
  },
  size: {
    validator(value: string) {
      return ['maximum', 'max', 'large', 'default', 'small', 'mini'].indexOf(value) > -1;
    },
    type: String,
    default: 'default'
  },
  block: {
    type: Boolean,
    default: false
  },
  text: {
    type: Boolean,
    default: false
  },
  href: {
    type: String,
    default: ''
  },
  to: {
    type: [String, Object],
    default: ''
  },
  target: {
    type: String,
    default: '_self'
  },
  plain: {
    type: Boolean,
    default: false
  },
  disabled: {
    type: Boolean,
    default: false
  }
});

const tag = computed(() => {
  if (props.href) {
    return 'a';
  }
  if (props.to) {
    return 'router-link';
  }
  return ElButton;
});

const typeMap: any = reactive({
  blue: 'primary',
  black: 'warning',
  gray: 'info',
  red: 'danger',
  green: 'success'
});

const sizeMap: any = reactive({
  maximum: 'large',
  max: 'large',
  large: 'large',
  default: 'default',
  small: 'small',
  mini: 'small'
});

const className = computed(() => {
  const str = [style.custom, style[props.type], style[props.size], props.block ? style.block : '', props.text ? style['is-text'] : '', props.disabled ? style['is-disabled'] : '', props.plain ? style['is-plain'] : ''];
  if (props.href || props.to) {
    return ['el-button', `el-button--${type.value}`, `el-button--${size.value}`, props.plain ? 'is-plain' : '', props.disabled ? 'is-disabled' : '', ...str];
  }
  return str;
});

const type = computed(() => {
  return 'default';
});

const size = computed(() => {
  return 'default';
});

</script>

<style lang="scss" module>
.red {
  --el-button-text-color: var(--text-text0);
  --el-button-bg-color: var(--red-red2);
  --el-button-border-color: var(--red-red2);
  --el-button-active-color: var(--red-red3);
  --el-button-hover-text-color: var(--text-text0);
  --el-button-hover-bg-color: var(--red-red3);
  --el-button-hover-border-color: var(--red-red3);
  --el-button-active-bg-color: var(--red-red1);
  --el-button-active-border-color: var(--red-red1);
  --el-button-disabled-text-color: var(--text-text4);
  --el-button-disabled-bg-color: var(--red-red4);
  --el-button-disabled-border-color: var(--red-red4);

  &.is-plain {
    background: none;
    --el-button-text-color: var(--red-red2);
    --el-button-border-color: var(--red-red2);
    --el-button-active-text-color: var(--red-red1);
    --el-button-hover-text-color: var(--red-red2);
    --el-button-hover-border-color: var(--red-red3);
    --el-button-disabled-text-color: var(--red-red1);
    --el-button-disabled-border-color: var(--red-red1);
    &.is-disabled{
      opacity: .6;
    }

    &:hover {
      --el-button-hover-text-color: var(--red-red3);
      --el-button-hover-border-color: var(--red-red3);
    }
  }

  &.is-text {
    --el-button-text-color: var(--red-red2);

    &:hover {
      --el-button-text-color: var(--red-red3);
    }

    &:active {
      --el-button-text-color: var(--red-red1);
    }
  }
}

.gray {
  --el-button-text-color: var(--text-text1);
  --el-button-bg-color: var(--fill-fill2);
  --el-button-border-color: var(--fill-fill2);
  --el-button-active-color: var(--gray-gray3);
  --el-button-hover-text-color: var(--text-text1);
  --el-button-hover-bg-color: var(--gray-gray2);
  --el-button-hover-border-color: var(--gray-gray2);
  --el-button-active-bg-color: var(--fill-fill2);
  --el-button-active-border-color: var(--gray-gray5);
  --el-button-disabled-text-color: var(--text-text4);
  --el-button-disabled-bg-color: var(--fill-fill2);
  --el-button-disabled-border-color: var(--fill-fill2);

  &.is-plain {
    background: none;
    --el-button-text-color: var(--text-text1);
    --el-button-border-color: var(--gray-gray3);
    --el-button-active-text-color: var(--text-text3);
    --el-button-hover-text-color: var(--text-text1);
    --el-button-hover-bg-color: var(--gray-gray2);
    --el-button-hover-border-color: var(--gray-gray2);

    &:hover {
      --el-button-hover-text-color: var(--text-text1);
      --el-button-hover-border-color: var(--gray-gray2);
    }
  }

  &.is-text {
    --el-button-text-color: var(--text-text1);

    // &:hover {
    //   --el-button-text-color: var(--gray-gray2);
    // }

    &:active {
      --el-button-text-color: var(--text-text1);
    }
  }
}

.blue {
  --el-button-text-color: var(--text-text0);
  --el-button-bg-color: var(--primary-primary3);
  --el-button-border-color: var(--primary-primary3);
  --el-button-active-color: var(--primary-primary1);
  --el-button-hover-text-color: var(--text-text0);
  --el-button-hover-bg-color: var(--primary-primary2);
  --el-button-hover-border-color: var(--primary-primary2);
  --el-button-active-bg-color: var(--primary-primary1);
  --el-button-active-border-color: var(--primary-primary1);
  --el-button-disabled-text-color: var(--text-text0);
  --el-button-disabled-bg-color: var(--fill-fill5);
  --el-button-disabled-border-color: var(--fill-fill5);
  font-weight: var(--el-button-font-weight) !important;
  font-size: var(--el-font-size-base) !important;
  --button-disabled-border:var();
  &.is-plain {

    --el-button-text-color: var(--primary-primary3);
    --el-button-border-color: var(--primary-primary3);
    --el-button-active-text-color: var(--primary-primary1);
    --el-button-hover-text-color: var(--primary-primary3);
    --el-button-hover-bg-color: var(--primary-primary2);
    --el-button-hover-border-color: var(--primary-primary2);

    background: none !important;
    color: var(--el-button-text-color) !important;
    border: 1px solid var(--el-button-text-color) !important;

    &.is-disabled{
      color: var(--el-button-text-color) !important;
    border: 1px solid var(--el-button-text-color) !important;
      opacity: .6;
    }

    &:hover {
      --el-button-hover-text-color: var(--primary-primary2);
      --el-button-hover-border-color: var(--primary-primary2);
    }
  }

  &.is-text {
    --el-button-text-color: var(--primary-primary3);

    &:hover {
      --el-button-text-color: var(--primary-primary2);
    }

    &:active {
      --el-button-text-color: var(--primary-primary1);
    }
  }
}

.black {
  --el-button-text-color: var(--text-text6);
  --el-button-bg-color: var(--gray-gray10);
  --el-button-border-color: var(--gray-gray10);
  --el-button-active-color: var(--gray-gray9);
  --el-button-hover-text-color: var(--text-text6);
  --el-button-hover-bg-color: var(--gray-gray9);
  --el-button-hover-border-color: var(--gray-gray9);
  --el-button-active-bg-color: var(--gray-gray9);
  --el-button-active-border-color: var(--gray-gray9);
  --el-button-disabled-text-color: var(--text-text4);
  --el-button-disabled-bg-color: var(--gray-gray2);
  --el-button-disabled-border-color: var(--gray-gray2);
  --el-button-active-text-color: var(--text-text4);

  &.is-plain {
    background: none;
    --el-button-text-color: var(--gray-gray10);
    --el-button-border-color: var(--gray-gray10);
    --el-button-active-text-color: var(--text-text3);
    --el-button-hover-text-color: var(--gray-gray10);
    --el-button-hover-border-color: var(--gray-gray9);

    &:hover {
      --el-button-hover-text-color: var(--gray-gray9);
      --el-button-hover-border-color: var(--gray-gray5);
    }
  }

  &.is-text {
    --el-button-text-color: var(--gray-gray10);

    &:hover {
      --el-button-text-color: var(--gray-gray9);
    }

    &:active {
      --el-button-text-color: var(--text-text3);
    }
  }
}

.green {
  --el-button-text-color: var(--text-text0);
  --el-button-bg-color: var(--green-green2);
  --el-button-border-color: var(--green-green2);
  --el-button-active-color: var(--green-green1);
  --el-button-hover-text-color: var(--text-text0);
  --el-button-hover-bg-color: var(--green-green3);
  --el-button-hover-border-color: var(--green-green3);
  --el-button-active-bg-color: var(--green-green1);
  --el-button-active-border-color: var(--green-green1);
  --el-button-disabled-text-color: var(--text-text4);
  --el-button-disabled-bg-color: var(--green-green4);
  --el-button-disabled-border-color: var(--green-green4);

  &.is-plain {
    background: none;
    --el-button-text-color: var(--green-green2);
    --el-button-border-color: var(--green-green2);
    --el-button-active-text-color: var(--green-green1);
    --el-button-hover-text-color: var(--green-green2);
    --el-button-hover-border-color: var(--green-green3);
    --el-button-disabled-text-color: var(--green-green1);
    --el-button-disabled-border-color: var(--green-green1);
    &.is-disabled{
      opacity: .6;
    }

    &:hover {
      --el-button-hover-text-color: var(--green-green3);
      --el-button-hover-border-color: var(--green-green3);
    }
  }

  &.is-text {
    --el-button-text-color: var(--green-green2);

    &:hover {
      --el-button-text-color: var(--green-green3);
    }

    &:active {
      --el-button-text-color: var(--green-green2);
    }
  }
}

// size
.maximum {
  --el-button-size: var(--height-maximum);
  --el-font-size-base: var(--font-size-maximum);
  --el-border-radius-base: var(--radius-maximum);
  --el-button-font-weight: var(--font-weight-maximum);
}

.max {
  --el-button-size: var(--height-max);
  --el-font-size-base: var(--font-size-max);
  --el-border-radius-base: var(--radius-max);
  --el-button-font-weight: var(--font-weight-max)
}

.large {
  --el-button-size: var(--height-large);
  --el-font-size-base: var(--font-size-max);
  --el-border-radius-base: var(--radius-large);
  --el-button-font-weight: var(--font-weight-large)
}

.default {
  --el-button-size: var(--height-default);
  --el-font-size-base: var(--font-size-default);
  --el-border-radius-base: var(--radius-default);
  --el-button-font-weight: var(--font-weight-default);
}

.small {
  --el-button-size: var(--height-small);
  --el-font-size-base: var(--font-size-small);
  --el-border-radius-base: var(--radius-small);
  --el-button-font-weight: var(--font-weight-small)
}

.mini {
  --el-button-size: var(--height-mini);
  --el-font-size-base: var(--font-size-mini);
  --el-border-radius-base: var(--radius-mini);
  --el-button-font-weight: var(--font-weight-mini)
}

//
.block {
  display: flex;
  width: 100%;
}

.custom {
  height:var(--el-button-size);
  &.is-disabled{
    background: var( --el-button-disabled-bg-color) !important;
    color:var(--el-button-disabled-text-color) !important;
  }
  &.is-plain {
    background: none !important;
  }

  &.is-text {
    background: none !important;
    border: none !important;

    color: var(--el-button-text-color) !important;

    &:hover {
      background: none !important;
      color: var(--el-button-text-color) !important;
    }

    &:active {
      color: var(--el-button-active-text-color) !important;
    }

    &.is-disabled {
      background: none !important;
      border: none !important;
      opacity: 0.6;
      color: var(--el-button-disabled-text-color) !important;
    }
  }

  &.is-plain.is-disabled{
    border: 1px solid var(--el-button-disabled-border-color) !important;
  }
  &:global(+.el-button){
     margin-left: 8px;
  }

  :global(.el-icon) + span {
    margin-inline-start: 8px;
  }
}

[dir=rtl] {
  .custom {
    &:global(+.el-button){
      margin-left: unset;
      margin-right: 8px;
    }
  }
}
</style>
